/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useState, useCallback } from "react";

//@Packages
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  PageLoader,
  Banner
} from "@forge/common";

//@Components
import Toast from "../../reusablecomponents/toast";
import SoftwareComponent from "./SoftwareComponent";

//@Models
import { ToastTypeProps } from "../../models";
import { SoftwareDetailsProps, ISoftwareProps } from "../../models/softwaredetails";

//@Store
import { RootState } from "../../store";

//@Services
import { 
  useGetSoftwareListQuery,
  useInstallSoftwareMutation,
  useUninstallSoftwareMutation,
  useReinstallSoftwareMutation,
  useLazyGetSoftwareLogsQuery,
  useLazyGetSoftwareDetailsQuery
} from "../../services/software-details-api";

//@Actions
import { clearSoftwareDetails, setSoftwareNotification } from "../../actions";

//@Utils
import { GatewayStatus } from "../../utils/constants";
import { gatewayErrorStatusMessages } from "../../utils/utility";

//@Styles
import "./index.scss";

const SoftwareDetails: React.FC<SoftwareDetailsProps> = ({
  gatewayId,
  gatewayType
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const convertFn = (value: string) => t(value);

  // Local state
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastTypeProps>("success");
  const [toastContent, setToastContent] = useState("");

  // Auto-hide toast after 5 seconds when shown
  useEffect(() => {
    if (showToast) {
      const AUTO_HIDE_MS = 5000;
      const timer = setTimeout(() => setShowToast(false), AUTO_HIDE_MS);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Redux state
  const { headerDetails, softwareDetails } = useSelector((state: RootState) => state.global);

  const getDeviceID = () => sessionStorage.getItem("systemGuid") || gatewayId || "";

  // API queries and mutations
  const {
    data: softwareData,
    isLoading: softwareLoading,
    error: softwareError,
    refetch: refetchSoftware
  } = useGetSoftwareListQuery(
    { gatewayId: getDeviceID() },
    { skip: !getDeviceID(), pollingInterval: 30000 }
  );

  const [installSoftware, { isLoading: installLoading }] = useInstallSoftwareMutation();
  const [uninstallSoftware, { isLoading: uninstallLoading }] = useUninstallSoftwareMutation();
  const [reinstallSoftware, { isLoading: reinstallLoading }] = useReinstallSoftwareMutation();
  const [triggerGetLogs] = useLazyGetSoftwareLogsQuery();
  const [triggerGetSoftwareDetails] = useLazyGetSoftwareDetailsQuery();

  // Initialize component
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      dispatch(clearSoftwareDetails());
    };
  }, [dispatch]);

  
  const handleInstallSoftware = useCallback(async (softwareKey: string, formData: any = null) => {
    try {
      await installSoftware({
        gatewayId: getDeviceID(),
        appKey: softwareKey,
        formData
      }).unwrap();
      
      setToastType("success");
      setToastContent(t("Software Installation Success"));
      setShowToast(true);
      refetchSoftware();
    } catch (error: any) {
      setToastType("error");
      setToastContent(gatewayErrorStatusMessages(convertFn, error?.message || "", error?.status));
      setShowToast(true);
    }
  }, [installSoftware, t, refetchSoftware, convertFn]);

  const handleUninstallSoftware = useCallback(async (softwareKey: string) => {
    console.log('handleUninstallSoftware called');
    try {
      await uninstallSoftware({
        gatewayId: getDeviceID(),
        appKey: softwareKey
      }).unwrap();

      console.log('uninstallSoftware success, calling refetchSoftware');
      setToastType("success");
      setToastContent(t("Software Uninstallation Success"));
      setShowToast(true);
      refetchSoftware();
    } catch (error: any) {
      setToastType("error");
      setToastContent(gatewayErrorStatusMessages(convertFn, error?.message || "", error?.status));
      setShowToast(true);
    }
  }, [uninstallSoftware, t, refetchSoftware, convertFn]);

  const handleReinstallSoftware = useCallback(async (softwareKey: string, formData: any = null) => {
    try {
      await reinstallSoftware({
        gatewayId: getDeviceID(),
        appKey: softwareKey,
        formData
      }).unwrap();
      
      setToastType("success");
      setToastContent(t("Software Reinstall Success"));
      setShowToast(true);
      refetchSoftware();
    } catch (error: any) {
      setToastType("error");
      setToastContent(gatewayErrorStatusMessages(convertFn, error?.message || "", error?.status));
      setShowToast(true);
    }
  }, [reinstallSoftware, t, refetchSoftware, convertFn]);

  const handleRefreshTableData = useCallback(() => {
    refetchSoftware();
  }, [refetchSoftware]);

  const handleFetchServiceLogs = useCallback(async (softwareKey: string): Promise<{ status: number; data: any }> => {
    try {
      const response = await triggerGetLogs({
        gatewayId: getDeviceID(),
        appKey: softwareKey
      }).unwrap();
      
      return {
        status: 200,
        data: response || []
      };
    } catch (error: any) {
      console.error("Failed to fetch service logs:", error);
      return {
        status: error?.status || 500,
        data: []
      };
    }
  }, [triggerGetLogs]);

  const handleGetSoftwareDetails = useCallback(async (softwareKey: string, status: string): Promise<any> => {
  try {
    const response = await triggerGetSoftwareDetails({
      gatewayId: getDeviceID(),
      appKey: softwareKey
    }).unwrap();
    
    return response || {};
  } catch (error: any) {
    console.error("Failed to get software data:", error);
    return {};
  }
}, [triggerGetSoftwareDetails]);

  // Check if gateway actions should be disabled
  // Actions enabled only when healthStatus is REGISTERED and status is CONNECTED or DISCONNECTED
  const checkIfGatewayActionsDisabled = useCallback(() => {
    const isHealthy = headerDetails?.healthStatus === "REGISTERED";
    const isValidStatus = headerDetails?.status === GatewayStatus.Active || 
                          headerDetails?.status === GatewayStatus.Error;
    return !(isHealthy && isValidStatus);
  }, [headerDetails]);

  // Check if any software action is in progress
  const isAnyActionInProgress = () => {
    return installLoading || uninstallLoading || reinstallLoading;
  };

  // Display mandatory software notification
  const displayNotification = () => {
    if (softwareDetails?.showMandatorySoftwareNotification && 
        softwareData?.edgeApps?.length > 0 &&
        !checkIfGatewayActionsDisabled()) {
      return (
        <Banner
          variant="inline"
          type="warning"
          title={t("Mandatory Software Installation")}
          description={t("Mandatory software installation is required.")}
          canClose={true}
        />
      );
    }
    return null;
  };

  // Handle error states
  if (softwareError) {
    const errorStatus = "status" in softwareError ? softwareError.status : 500;
    return (
      <div className="software-details-error">
        <Banner 
          variant="inline" 
          type="critical"
          title=""
          description={gatewayErrorStatusMessages(convertFn, t("Software details error"), errorStatus)}
          canClose={false}
        />
      </div>
    );
  }

  const softwareComponentProps: ISoftwareProps = {
    softwares: softwareData?.edgeApps || [],
    gatewayType: gatewayType,
    disableGatewayActions: checkIfGatewayActionsDisabled(),
    installService: handleInstallSoftware,
    unInstallService: handleUninstallSoftware,
    reinstallService: handleReinstallSoftware,
    fetchServiceLogs: handleFetchServiceLogs,
    refreshTableData: handleRefreshTableData,
    openInstallationForm: false,
    getSoftwareDetails: handleGetSoftwareDetails,
    fcAppInstallService: handleInstallSoftware,
    fcAppUnInstallService: handleUninstallSoftware,
    getTargetForgePoints: (softwareId: number, softwareKey: string) => {
      console.log("Getting target forge points for:", softwareKey);
    }
  };

  // Main render
  return (
    <div className="software-details-container">
      <PageLoader 
        isLoading={softwareLoading || isAnyActionInProgress()} 
        content={t("Loading")}
      />
      
      <div className="tab-wrapper">
        {/* Device notification banner */}
        {headerDetails && 'displayNotification' in headerDetails && headerDetails.displayNotification && (
          <Banner
            variant="inline"
            type="info"
            title={t("Update Device")}
            description={t("Device Updated successfully")}
            canClose={false}
          />
        )}

        {/* Mandatory software notification */}
        {displayNotification()}

        <div className="software-table-wrapper">
          {/* Software component */}
          <div className="software-component-container">
            {softwareData?.edgeApps && softwareData.edgeApps.length > 0 ? (
              <SoftwareComponent {...softwareComponentProps} />
            ) : (
              !softwareLoading && (
                <div className="no-software-message">
                  <Banner
                    variant="inline"
                    type="info"
                    title={t("No Software Found")}
                    description={t("No software applications are currently installed on this gateway.")}
                    canClose={false}
                  />
                </div>
              )
            )}
          </div>
        </div>

        {/* Toast notifications */}
        {showToast && (
          <div className="toast-container">
            <div onClick={() => setShowToast(false)} style={{ cursor: "pointer" }}>
              <Toast
                id="software-action-toast"
                type={toastType}
                content=""
                lifetime={5000}
                showClose={true}
                title={toastType === "success" ? t("Success") : t("Error")}
                subtitle={toastContent}
                showDismiss={false}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftwareDetails;